import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactDatatable from "@mkikets/react-datatable";
import { useGetUserStakingQuery, useLazyGetUserStakingTransactionsQuery, useGetAllUsersListQuery } from "../../../redux/userApi";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Button, Modal, Table, Row, Col, Form } from "react-bootstrap";
import { useGetStakingPlanQuery } from "../../../redux/stakingPlanApi";
import { useGetCryptocurrencyQuery } from "../../../redux/cryptocurrencyApi";
import ViewStakingPlan from "../../partial/ViewStakingPlan";
import Select from 'react-select';

function UserStaking() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [params, setParams] = useState({
        page: 1,
        limit: 10,
        user_id: id || "",
        staking_type: ""
    });

    const [filters, setFilters] = useState({
        user_id: id || "",
        staking_type: ""
    });

    const { data: apiResponse, isLoading, isFetching } = useGetUserStakingQuery(params);
    const stakings = apiResponse?.data || [];
    const totalRecords = apiResponse?.totalCount || 0;

    const { data: userResponse } = useGetAllUsersListQuery();
    const users = userResponse?.data || [];

    const userOptions = [
        { value: "", label: t('select_user') || "All Users" },
        ...users.map(user => ({
            value: user.id,
            label: String(user.mobile || user.email || user.fname || user.id)
        }))
    ];

    const stakingTypeOptions = [
        { value: "", label: t('all_types') || "All Types" },
        { value: "GENERAL_STAKING", label: t('general_staking') || "General Staking" },
        { value: "SWAP_STAKING", label: t('swap_staking') || "Swap Staking" }
    ];

    const customStyles = {
        control: (provided) => ({ ...provided, color: '#000' }),
        singleValue: (provided) => ({ ...provided, color: '#000' }),
        input: (provided) => ({ ...provided, color: '#000' }),
        option: (provided, state) => ({
            ...provided,
            color: '#000',
            backgroundColor: state.isFocused ? '#e9ecef' : '#fff',
        }),
        menu: (provided) => ({ ...provided, backgroundColor: '#fff' })
    };

    const handleUserSelect = (selectedOption) => {
        setFilters(prev => ({ ...prev, user_id: selectedOption ? selectedOption.value : "" }));
    };

    const handleTypeSelect = (selectedOption) => {
        setFilters(prev => ({ ...prev, staking_type: selectedOption ? selectedOption.value : "" }));
    };

    const handleSearchSubmit = () => {
        setParams({
            ...params,
            page: 1,
            user_id: filters.user_id,
            staking_type: filters.staking_type
        });
    };

    const handleClearFilters = () => {
        setFilters({ user_id: "", staking_type: "" });
        setParams({ page: 1, limit: 10, user_id: "", staking_type: "" });
    };

    const handleTableChange = (newParams) => {
        setParams({
            ...params,
            page: newParams.page_number,
            limit: newParams.page_size,
        });
    };

    const { data: planResponse } = useGetStakingPlanQuery();
    const { data: cryptoResponse } = useGetCryptocurrencyQuery();
    const plans = planResponse?.data || [];
    const cryptocurrencies = cryptoResponse?.data || [];

    const [showPlanModal, setShowPlanModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState({});

    const handleShowPlan = (planId) => {
        const plan = plans.find(p => p.id === planId);
        if (plan) {
            setSelectedPlan(plan);
            setShowPlanModal(true);
        }
    };

    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedStakingDetails, setSelectedStakingDetails] = useState(null);

    const [showRewardModal, setShowRewardModal] = useState(false);
    const [rewardTransactions, setRewardTransactions] = useState([]);
    const [fetchRewards, { isLoading: isRewardsLoading }] = useLazyGetUserStakingTransactionsQuery();

    const handleShowRewards = async (record) => {
        setShowRewardModal(true);
        const res = await fetchRewards({ user_staking_id: record.staking_id });
        if (res?.data?.status) {
            setRewardTransactions(res.data.data || []);
        } else {
            setRewardTransactions([]);
        }
    };

    const handleCloseRewards = () => {
        setShowRewardModal(false);
        setRewardTransactions([]);
    };

    const handleShowDetails = (record) => {
        setSelectedStakingDetails(record);
        setShowDetailsModal(true);
    };

    const handleCloseDetails = () => {
        setShowDetailsModal(false);
        setSelectedStakingDetails(null);
    };

    const truncateHash = (hash) => {
        if (!hash) return "-";
        if (hash.length <= 8) return hash;
        return `${hash.substring(0, 4)}.....${hash.substring(hash.length - 4)}`;
    };

    const columns = [
        {
            key: "srno.",
            text: t('sr_no') || "Sr No.",
            className: "sr_no.",
            align: "left",
            sortable: true,
            cell: (row, index) => (params.page - 1) * params.limit + index + 1,
        },
        {
            key: "user",
            text: t('user') || "User (Email/Phone)",
            className: "user",
            align: "left",
            sortable: true,
            cell: (record) => (
                <div>
                    <div>{record.user_email || '-'}</div>
                    <div>{record.user_mobile || '-'}</div>
                </div>
            )
        },
        {
            key: "staking_type",
            text: t('staking_type') || "Type",
            className: "staking_type",
            align: "left",
            sortable: true,
            cell: (record) => (
                <span
                    className="text-primary fw-bold"
                    style={{ cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => handleShowPlan(record.staking_plan_id)}
                >
                    {record.staking_type?.replace('_', ' ') || "-"}
                </span>
            )
        },
        {
            key: "swap_amount",
            text: t('swap_amount') || "Swap Amount",
            className: "swap_amount",
            align: "left",
            sortable: true,
            cell: (record) => {
                if (!record.swap_staking_cryptocurrency_amount) return "-";
                return `${record.swap_staking_cryptocurrency_amount} ${record.swap_cryptocurrency_symbol || ''} ${record.swap_network_symbol ? `(${record.swap_network_symbol})` : ''}`.trim();
            }
        },
        {
            key: "staking_amount",
            text: t('staking_amount') || "Staking Amount",
            className: "staking_amount",
            align: "left",
            sortable: true,
            cell: (record) => {
                if (!record.staking_amount) return "-";
                return `${record.staking_amount} ${record.staking_cryptocurrency_symbol || ''} ${record.staking_network_symbol ? `(${record.staking_network_symbol})` : ''}`.trim();
            }
        },
        {
            key: "tx_hash",
            text: t('tx_hash') || "Tx Hash",
            className: "tx_hash",
            align: "left",
            sortable: true,
            cell: (record) => {
                if (record.staking_type === 'swap_staking' && record.swap_staking_tx_hash) {
                    return <a href={record.swap_staking_tx_hash_url} className="text-primary" target="_blank" rel="noreferrer">{truncateHash(record.swap_staking_tx_hash)}</a>;
                } else if (record.staking_type === 'general_staking' && record.staking_tx_hash) {
                    return <a href={record.staking_tx_hash_url} className="text-primary" target="_blank" rel="noreferrer">{truncateHash(record.staking_tx_hash)}</a>;
                }
                return "-";
            }
        },
        {
            key: "created_at",
            text: t('created_at') || "Date",
            className: "created_at",
            align: "left",
            sortable: true,
            cell: (record) => {
                const date = record.created_at;
                return <>{date ? moment(date).format("DD/MM/YYYY HH:mm:ss") : ""}</>;
            },
        },
        {
            key: "action",
            text: t('action') || "Action",
            className: "action",
            align: "left",
            sortable: false,
            cell: (record) => (
                <div className="d-flex gap-2">
                    <Button variant="info" size="sm" onClick={() => handleShowDetails(record)}>
                        {t('show_details') || 'Show Details'}
                    </Button>
                    <Button variant="success" size="sm" onClick={() => handleShowRewards(record)}>
                        {t('rewards') || 'Rewards'}
                    </Button>
                </div>
            )
        },
    ];

    const config = {
        page_size: 10,
        length_menu: [10, 20, 50],
        filename: "User Staking List",
        no_data_text: t('no_record_found') || "No records found",
        language: {
            filter: t('search_in_records') || "Search in records...",
            pagination: {
                first: t('first') || "First",
                previous: t('previous') || "Previous",
                next: t('next') || "Next",
                last: t('last') || "Last",
            },
        },
        show_length_menu: true,
        show_filter: false,
        show_pagination: true,
        show_info: true,
    };

    return (
        <section className="section dashboard">
            <div className="pagetitle d-flex align-items-center justify-content-between mb-4">
                <div className="d-flex align-items-center gap-2">
                    <Button variant="secondary" onClick={() => navigate(-1)}>
                        {t('back') || "Back"}
                    </Button>
                    <h1>{t('user_staking_list') || "User Staking List"}</h1>
                </div>
            </div>

            <div className="card mb-4">
                <div className="card-body pt-4">
                    <Row className="mb-3">
                        <Col md={4}>
                            <Form.Group controlId="filterUserId">
                                <Form.Label>{t('user_id') || "User ID"}</Form.Label>
                                <Select
                                    options={userOptions}
                                    value={userOptions.find(opt => opt.value === filters.user_id) || userOptions[0]}
                                    onChange={handleUserSelect}
                                    isSearchable
                                    styles={customStyles}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group controlId="filterStakingType">
                                <Form.Label>{t('staking_type') || "Staking Type"}</Form.Label>
                                <Select
                                    options={stakingTypeOptions}
                                    value={stakingTypeOptions.find(opt => opt.value === filters.staking_type) || stakingTypeOptions[0]}
                                    onChange={handleTypeSelect}
                                    isSearchable
                                    styles={customStyles}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4} className="d-flex align-items-end gap-2">
                            <Button variant="primary" onClick={handleSearchSubmit}>
                                {t('search') || "Search"}
                            </Button>
                            <Button variant="secondary" onClick={handleClearFilters}>
                                {t('clear') || "Clear"}
                            </Button>
                        </Col>
                    </Row>
                </div>
            </div>

            <div className="card">
                <div className="card-body pt-4 position-relative">
                    {(isLoading || isFetching) && (
                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 10 }}>
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    )}
                    <ReactDatatable
                        key={`datatable-user-staking-${params.page}-${params.limit}`}
                        config={config}
                        records={stakings}
                        columns={columns}
                        dynamic={true}
                        total_record={totalRecords}
                        onChange={handleTableChange}
                    />
                </div>
            </div>

            <Modal show={showDetailsModal} onHide={handleCloseDetails} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t('staking_details') || 'Staking Details'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedStakingDetails && (
                        <Table striped bordered hover>
                            <tbody>
                                <tr>
                                    <td>{t('user') || 'User'}</td>
                                    <td>{selectedStakingDetails.user_email || '-'} / {selectedStakingDetails.user_mobile || '-'}</td>
                                </tr>
                                <tr>
                                    <td>{t('refer_user') || 'Refer User'}</td>
                                    <td>{selectedStakingDetails.refer_email || '-'} / {selectedStakingDetails.refer_mobile || '-'}</td>
                                </tr>
                                <tr>
                                    <td>{t('staking_type') || 'Type'}</td>
                                    <td>{selectedStakingDetails.staking_type?.replace('_', ' ')}</td>
                                </tr>
                                <tr>
                                    <td>{t('staking_amount') || 'Staking Amount'}</td>
                                    <td>{selectedStakingDetails.staking_amount || '0'} {selectedStakingDetails.staking_cryptocurrency_symbol || ''} {selectedStakingDetails.staking_network_symbol ? `(${selectedStakingDetails.staking_network_symbol})` : ''}</td>
                                </tr>
                                <tr>
                                    <td>{t('swap_amount') || 'Swap Amount'}</td>
                                    <td>{selectedStakingDetails.swap_staking_cryptocurrency_amount || '0'} {selectedStakingDetails.swap_cryptocurrency_symbol || ''} {selectedStakingDetails.swap_network_symbol ? `(${selectedStakingDetails.swap_network_symbol})` : ''}</td>
                                </tr>
                                <tr>
                                    <td>{t('stake_fee') || 'Stake Fee'}</td>
                                    <td>{selectedStakingDetails.stake_fee_amount_paid || '0'} {selectedStakingDetails.stake_fee_cryptocurrency_symbol || ''} {selectedStakingDetails.stake_fee_network_symbol ? `(${selectedStakingDetails.stake_fee_network_symbol})` : ''}</td>
                                </tr>
                                <tr>
                                    <td>{t('swap_staking_tx_hash') || 'Swap Tx Hash'}</td>
                                    <td>
                                        {selectedStakingDetails.swap_staking_tx_hash ? (
                                            <a href={selectedStakingDetails.swap_staking_tx_hash_url} className="text-primary" target="_blank" rel="noreferrer">
                                                {truncateHash(selectedStakingDetails.swap_staking_tx_hash)}
                                            </a>
                                        ) : '-'}
                                    </td>
                                </tr>
                                <tr>
                                    <td>{t('staking_tx_hash') || 'Staking Tx Hash'}</td>
                                    <td>
                                        {selectedStakingDetails.staking_tx_hash ? (
                                            <a href={selectedStakingDetails.staking_tx_hash_url} className="text-primary" target="_blank" rel="noreferrer">
                                                {truncateHash(selectedStakingDetails.staking_tx_hash)}
                                            </a>
                                        ) : '-'}
                                    </td>
                                </tr>
                                <tr>
                                    <td>{t('stake_fee_tx_hash') || 'Stake Fee Tx Hash'}</td>
                                    <td>
                                        {selectedStakingDetails.stake_fee_tx_hash ? (
                                            <a href={selectedStakingDetails.stake_fee_tx_hash_url} className="text-primary" target="_blank" rel="noreferrer">
                                                {truncateHash(selectedStakingDetails.stake_fee_tx_hash)}
                                            </a>
                                        ) : '-'}
                                    </td>
                                </tr>
                                <tr>
                                    <td>{t('status') || 'Status'}</td>
                                    <td>{selectedStakingDetails.status}</td>
                                </tr>
                                <tr>
                                    <td>{t('created_at') || 'Date'}</td>
                                    <td>{selectedStakingDetails.created_at ? moment(selectedStakingDetails.created_at).format("DD/MM/YYYY HH:mm:ss") : '-'}</td>
                                </tr>
                            </tbody>
                        </Table>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseDetails}>
                        {t('close') || 'Close'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showRewardModal} onHide={handleCloseRewards} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t('reward_transactions') || 'Reward Transactions'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isRewardsLoading ? (
                        <p>{t('loading') || "Loading..."}</p>
                    ) : rewardTransactions.length > 0 ? (
                        <Table striped bordered hover responsive>
                            <thead>
                                <tr>
                                    <th>{t('sr_no') || 'Sr No.'}</th>
                                    <th>{t('amount') || 'Amount'}</th>
                                    <th>{t('type') || 'Type'}</th>
                                    <th>{t('tx_hash') || 'Tx Hash'}</th>
                                    <th>{t('date') || 'Date'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rewardTransactions.map((tx, index) => (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>{tx.amount} {tx.cryptocurrency_symbol || ''} {tx.network_symbol ? `(${tx.network_symbol})` : ''}</td>
                                        <td>{tx.tx_type}</td>
                                        <td>{truncateHash(tx.tx_hash)}</td>
                                        <td>{tx.created_at ? moment(tx.created_at).format("DD/MM/YYYY HH:mm:ss") : '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    ) : (
                        <p>{t('no_record_found') || "No records found"}</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseRewards}>
                        {t('close') || 'Close'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <ViewStakingPlan
                show={showPlanModal}
                onHide={() => setShowPlanModal(false)}
                viewData={selectedPlan}
                cryptocurrencies={cryptocurrencies}
            />
        </section>
    );
}

export default UserStaking;
